import {
  Activity,
  Archive,
  Pencil,
  Plus,
  Search,
  Trash2,
  UserRoundCheck,
  UsersRound,
  X,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, type SyntheticEvent } from "react";
import {
  archiveTenantRewardSegment,
  createTenantRewardSegment,
  fetchTenantRewardSegments,
  messageFromError,
  updateTenantRewardSegment,
  type TenantRewardSegment,
  type TenantRewardSegmentInput,
  type TenantRewardSegmentRule,
} from "../api";
import { AppAlert } from "../components/AppAlertDialog";

const segmentsKey = ["tenant-reward-segments"] as const;
const rewardAuditKey = ["tenant-reward-audit"] as const;

type FieldKind = "cohort" | "boolean" | "number";

interface SegmentFieldDefinition {
  key: string;
  dimension: TenantRewardSegmentRule["dimension"];
  field: string;
  label: string;
  detail: string;
  kind: FieldKind;
}

const fieldDefinitions: SegmentFieldDefinition[] = [
  { key: "CUSTOMER.lifecycleCohort", dimension: "CUSTOMER", field: "lifecycleCohort", label: "Lifecycle cohort", detail: "New, repeat, birthday, or anniversary members", kind: "cohort" },
  { key: "CUSTOMER.emailVerified", dimension: "CUSTOMER", field: "emailVerified", label: "Email verified", detail: "Whether the member has verified their email", kind: "boolean" },
  { key: "CUSTOMER.membershipAgeDays", dimension: "CUSTOMER", field: "membershipAgeDays", label: "Membership age", detail: "Days since joining this Business Owner program", kind: "number" },
  { key: "CUSTOMER.verifiedTransactionCount", dimension: "CUSTOMER", field: "verifiedTransactionCount", label: "Confirmed purchases", detail: "Number of confirmed Business Owner transactions", kind: "number" },
  { key: "CUSTOMER.verifiedSpendMinor", dimension: "CUSTOMER", field: "verifiedSpendMinor", label: "Confirmed spend", detail: "Confirmed spend in minor currency units", kind: "number" },
  { key: "CUSTOMER.daysSinceLastVerifiedTransaction", dimension: "CUSTOMER", field: "daysSinceLastVerifiedTransaction", label: "Days since last purchase", detail: "Days since the latest confirmed purchase", kind: "number" },
  { key: "TRANSACTION.count", dimension: "TRANSACTION", field: "count", label: "Transaction count", detail: "Confirmed transaction count used by reward eligibility", kind: "number" },
  { key: "TRANSACTION.totalSpendMinor", dimension: "TRANSACTION", field: "totalSpendMinor", label: "Transaction spend total", detail: "Confirmed qualifying spend in minor units", kind: "number" },
  { key: "TRANSACTION.daysSinceLast", dimension: "TRANSACTION", field: "daysSinceLast", label: "Transaction recency", detail: "Days since the latest confirmed transaction", kind: "number" },
];

const cohortOptions = [
  ["ALL_MEMBERS", "All members"],
  ["NEW_MEMBERS", "New members (30 days)"],
  ["FIRST_TIME_BUYERS", "First-time buyers"],
  ["REPEAT_CUSTOMERS", "Repeat customers"],
  ["BIRTHDAY", "Birthday today"],
  ["ANNIVERSARY", "Membership anniversary"],
] as const;

const numberOperators: Array<[TenantRewardSegmentRule["operator"], string]> = [
  ["GTE", "At least"],
  ["LTE", "At most"],
  ["EQ", "Exactly"],
  ["NEQ", "Not equal to"],
  ["BETWEEN", "Between"],
];

const fieldForRule = (rule: TenantRewardSegmentRule) =>
  fieldDefinitions.find((field) => field.dimension === rule.dimension && field.field === rule.field)
  ?? fieldDefinitions[0];

const makeRule = (field = fieldDefinitions[0]): TenantRewardSegmentRule => {
  if (!field || field.kind === "cohort") {
    return { dimension: "CUSTOMER", field: "lifecycleCohort", operator: "IN", value: ["ALL_MEMBERS"] };
  }
  if (field.kind === "boolean") {
    return { dimension: "CUSTOMER", field: "emailVerified", operator: "EQ", value: true };
  }
  return { dimension: field.dimension, field: field.field, operator: "GTE", value: 1 };
};

const formatNumber = (value: number) => new Intl.NumberFormat("en-US").format(value);
const formatDateTime = (value: string | null) => {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const operatorLabel = (operator: string) => ({
  EQ: "is",
  NEQ: "is not",
  GTE: "is at least",
  LTE: "is at most",
  BETWEEN: "is between",
  IN: "includes",
  NOT_IN: "excludes",
}[operator] ?? operator);

const valueLabel = (rule: TenantRewardSegmentRule) => {
  if (typeof rule.value === "boolean") return rule.value ? "Yes" : "No";
  if (typeof rule.value === "number") return formatNumber(rule.value);
  if (Array.isArray(rule.value)) {
    return rule.value.map((value) => typeof value === "number"
      ? formatNumber(value)
      : value.toLowerCase().replaceAll("_", " ")).join(" – ");
  }
  return "Configured value";
};

function SegmentRuleEditor({
  rule,
  index,
  canRemove,
  update,
  remove,
}: {
  rule: TenantRewardSegmentRule;
  index: number;
  canRemove: boolean;
  update: (rule: TenantRewardSegmentRule) => void;
  remove: () => void;
}) {
  const field = fieldForRule(rule) ?? fieldDefinitions[0];
  if (!field) return null;
  const numberValue = typeof rule.value === "number" ? rule.value : 0;
  const rangeValue = Array.isArray(rule.value) && rule.value.length === 2
    && typeof rule.value[0] === "number" && typeof rule.value[1] === "number"
    ? rule.value as [number, number]
    : [0, 0] as [number, number];
  return <fieldset className="segment-rule-card">
    <legend>Rule {String(index + 1)}</legend>
    <label className="segment-rule-field">
      <span>Member fact</span>
      <select value={field.key} onChange={(event) => {
        const next = fieldDefinitions.find((item) => item.key === event.target.value);
        if (next) update(makeRule(next));
      }}>
        {fieldDefinitions.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
      </select>
      <small>{field.detail}</small>
    </label>
    <label>
      <span>Comparison</span>
      {field.kind === "cohort" ? <select value={rule.operator} onChange={(event) => {
        update({ ...rule, operator: event.target.value as "IN" | "NOT_IN" });
      }}><option value="IN">Includes</option><option value="NOT_IN">Excludes</option></select>
        : field.kind === "boolean" ? <select value={rule.operator} onChange={(event) => {
          update({ ...rule, operator: event.target.value as "EQ" | "NEQ" });
        }}><option value="EQ">Is</option><option value="NEQ">Is not</option></select>
          : <select value={rule.operator} onChange={(event) => {
            const operator = event.target.value as TenantRewardSegmentRule["operator"];
            update({
              ...rule,
              operator,
              value: operator === "BETWEEN" ? [numberValue, numberValue] : numberValue,
            });
          }}>{numberOperators.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>}
    </label>
    {field.kind === "cohort" && <label>
      <span>Cohort</span>
      <select value={Array.isArray(rule.value) ? String(rule.value[0]) : "ALL_MEMBERS"} onChange={(event) => {
        update({ ...rule, value: [event.target.value] });
      }}>{cohortOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
    </label>}
    {field.kind === "boolean" && <label>
      <span>Value</span>
      <select value={rule.value === true ? "true" : "false"} onChange={(event) => {
        update({ ...rule, value: event.target.value === "true" });
      }}><option value="true">Yes</option><option value="false">No</option></select>
    </label>}
    {field.kind === "number" && rule.operator !== "BETWEEN" && <label>
      <span>Value</span>
      <input min="0" step="1" type="number" value={String(numberValue)} onChange={(event) => {
        update({ ...rule, value: Math.max(0, Math.round(Number(event.target.value || 0))) });
      }} />
    </label>}
    {field.kind === "number" && rule.operator === "BETWEEN" && <div className="segment-range-fields">
      <label><span>Minimum</span><input min="0" step="1" type="number" value={String(rangeValue[0])} onChange={(event) => {
        update({ ...rule, value: [Math.max(0, Math.round(Number(event.target.value || 0))), rangeValue[1]] });
      }} /></label>
      <label><span>Maximum</span><input min="0" step="1" type="number" value={String(rangeValue[1])} onChange={(event) => {
        update({ ...rule, value: [rangeValue[0], Math.max(0, Math.round(Number(event.target.value || 0)))] });
      }} /></label>
    </div>}
    <button className="segment-remove-rule" type="button" disabled={!canRemove} aria-label={`Remove rule ${String(index + 1)}`} onClick={remove}><Trash2 /> Remove</button>
  </fieldset>;
}

function SegmentForm({
  segment,
  close,
  saved,
}: {
  segment?: TenantRewardSegment;
  close: () => void;
  saved: () => Promise<void>;
}) {
  const [name, setName] = useState(segment?.name ?? "");
  const [description, setDescription] = useState(segment?.description ?? "");
  const [status, setStatus] = useState<"draft" | "active">(
    segment?.status === "active" ? "active" : "draft",
  );
  const [match, setMatch] = useState<"ALL" | "ANY">(segment?.conditions.match ?? "ALL");
  const [rules, setRules] = useState<TenantRewardSegmentRule[]>(
    segment?.conditions.rules.length ? segment.conditions.rules : [makeRule()],
  );
  const [error, setError] = useState("");
  const mutation = useMutation({
    mutationFn: async (input: TenantRewardSegmentInput) => segment
      ? updateTenantRewardSegment(segment.publicId, input)
      : createTenantRewardSegment(input),
  });
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    for (const rule of rules) {
      if (
        rule.operator === "BETWEEN"
        && Array.isArray(rule.value)
        && typeof rule.value[0] === "number"
        && typeof rule.value[1] === "number"
        && rule.value[0] > rule.value[1]
      ) {
        setError("Each range minimum must be less than or equal to its maximum.");
        return;
      }
    }
    try {
      await mutation.mutateAsync({
        name: name.trim(),
        description: description.trim(),
        status,
        conditions: { match, rules },
      });
      await saved();
      close();
    } catch (caught) {
      setError(messageFromError(caught));
    }
  };
  return <form className="segment-editor" onSubmit={(event) => { void submit(event); }}>
    <div className="segment-editor__heading">
      <div><p className="eyebrow">Structured audience</p><h3>{segment ? "Edit segment" : "Create segment"}</h3><p>Rules use verified Business Owner facts only. Expressions and custom SQL are never accepted.</p></div>
      <button className="icon-control" type="button" aria-label="Close segment editor" onClick={close}><X /></button>
    </div>
    {error && <AppAlert message={error} onDismiss={() => { setError(""); }} />}
    <div className="segment-editor__details">
      <label><span>Segment name</span><input required minLength={2} maxLength={160} value={name} onChange={(event) => { setName(event.target.value); }} placeholder="Repeat customers" /></label>
      <label><span>Status</span><select value={status} onChange={(event) => { setStatus(event.target.value as "draft" | "active"); }}><option value="draft">Draft</option><option value="active">Active</option></select></label>
      <label className="segment-editor__description"><span>Description</span><textarea maxLength={1000} rows={3} value={description} onChange={(event) => { setDescription(event.target.value); }} placeholder="Who belongs in this audience and why?" /></label>
    </div>
    <div className="segment-match-control">
      <div><strong>Match logic</strong><span>Choose whether every rule or at least one rule must match.</span></div>
      <div role="group" aria-label="Segment rule match logic">
        <button className={match === "ALL" ? "is-selected" : ""} type="button" aria-pressed={match === "ALL"} onClick={() => { setMatch("ALL"); }}>Match all</button>
        <button className={match === "ANY" ? "is-selected" : ""} type="button" aria-pressed={match === "ANY"} onClick={() => { setMatch("ANY"); }}>Match any</button>
      </div>
    </div>
    <div className="segment-rule-list">{rules.map((rule, index) => <SegmentRuleEditor
      key={`${rule.dimension}:${rule.field}:${String(index)}`}
      rule={rule}
      index={index}
      canRemove={rules.length > 1}
      update={(next) => { setRules((current) => current.map((item, itemIndex) => itemIndex === index ? next : item)); }}
      remove={() => { setRules((current) => current.filter((_, itemIndex) => itemIndex !== index)); }}
    />)}</div>
    <div className="segment-editor__actions">
      <button className="button button--outline" type="button" onClick={() => { setRules((current) => [...current, makeRule()]); }}><Plus /> Add rule</button>
      <span />
      <button className="button button--quiet" type="button" onClick={close}>Cancel</button>
      <button className="button button--primary" disabled={mutation.isPending || !name.trim()} type="submit">{mutation.isPending ? "Saving…" : segment ? "Save changes" : "Create segment"}</button>
    </div>
  </form>;
}

export function TenantRewardSegmentsView() {
  const queryClient = useQueryClient();
  const segmentsQuery = useQuery({ queryKey: segmentsKey, queryFn: fetchTenantRewardSegments });
  const [editing, setEditing] = useState<TenantRewardSegment | "new" | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("current");
  const [actionError, setActionError] = useState("");
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: segmentsKey }),
      queryClient.invalidateQueries({ queryKey: rewardAuditKey }),
    ]);
  };
  const archiveMutation = useMutation({
    mutationFn: archiveTenantRewardSegment,
    onSuccess: refresh,
    onError: (caught) => { setActionError(messageFromError(caught)); },
  });
  const filtered = useMemo(() => (segmentsQuery.data ?? []).filter((segment) => {
    const matchesSearch = !search.trim()
      || `${segment.name} ${segment.description}`.toLowerCase().includes(search.trim().toLowerCase());
    const matchesStatus = status === "all"
      || (status === "current" ? segment.status !== "archived" : segment.status === status);
    return matchesSearch && matchesStatus;
  }), [search, segmentsQuery.data, status]);

  return <section className="portal-panel segment-workspace">
    <div className="panel-heading segment-workspace__heading">
      <div><p className="eyebrow">Audience strategy</p><h2>Customer segments</h2><p>Build reusable audiences from membership and confirmed transaction facts, then connect them to reward campaigns.</p></div>
      <button className="button button--primary button--compact" type="button" onClick={() => { setEditing("new"); }}><Plus /> New segment</button>
    </div>
    {(segmentsQuery.isError || actionError) && <AppAlert message={actionError || messageFromError(segmentsQuery.error)} onDismiss={() => { setActionError(""); }} />}
    {editing === "new" && <SegmentForm close={() => { setEditing(null); }} saved={refresh} />}
    {editing && editing !== "new" && <SegmentForm key={editing.publicId} segment={editing} close={() => { setEditing(null); }} saved={refresh} />}
    <div className="segment-toolbar">
      <label><Search /><span className="sr-only">Search customer segments</span><input type="search" value={search} onChange={(event) => { setSearch(event.target.value); }} placeholder="Search segments" /></label>
      <label><span>Status</span><select value={status} onChange={(event) => { setStatus(event.target.value); }}><option value="current">Current</option><option value="active">Active</option><option value="draft">Draft</option><option value="archived">Archived</option><option value="all">All</option></select></label>
    </div>
    {segmentsQuery.isLoading ? <div className="segment-state" role="status"><Activity className="spinner" /><strong>Loading customer segments</strong></div>
      : filtered.length ? <div className="segment-card-grid">{filtered.map((segment) => <article className={`segment-card segment-card--${segment.status}`} key={segment.publicId}>
        <div className="segment-card__heading"><span><UsersRound /></span><div><small>{segment.conditions.match === "ALL" ? "All rules" : "Any rule"}</small><h3>{segment.name}</h3></div><b>{segment.status}</b></div>
        <p>{segment.description || "No description provided."}</p>
        <div className="segment-estimate"><UserRoundCheck /><div><small>Approx. members</small><strong>{formatNumber(segment.estimatedMembers)}</strong></div><span>As of {formatDateTime(segment.estimate.asOf)}</span></div>
        <div className="segment-condition-summary">{segment.conditions.rules.map((rule, index) => {
          const field = fieldForRule(rule);
          return <span key={`${rule.dimension}:${rule.field}:${String(index)}`}><strong>{field?.label ?? rule.field}</strong> {operatorLabel(rule.operator)} {valueLabel(rule)}</span>;
        })}</div>
        <small className="segment-estimate-note">Estimate only · recalculated when this segment is saved. {segment.estimate.basis}</small>
        <div className="segment-card__actions">
          {segment.status !== "archived" && <button type="button" onClick={() => { setEditing(segment); }}><Pencil /> Edit</button>}
          {segment.status !== "archived" && <button className="is-danger" disabled={archiveMutation.isPending} type="button" onClick={() => {
            if (window.confirm(`Archive ${segment.name}? Existing rewards that require it will fail closed until their audience is changed.`)) {
              setActionError("");
              archiveMutation.mutate(segment.publicId);
            }
          }}><Archive /> Archive</button>}
        </div>
      </article>)}</div>
        : <div className="segment-state segment-state--empty"><span><UsersRound /></span><strong>{segmentsQuery.data?.length ? "No segments match these filters" : "Create your first customer segment"}</strong><p>{segmentsQuery.data?.length ? "Adjust the search or status filter." : "Start with verified membership and purchase facts—no custom expressions or imported demo audiences."}</p>{!segmentsQuery.data?.length && <button className="button button--outline" type="button" onClick={() => { setEditing("new"); }}>Create segment</button>}</div>}
  </section>;
}
